// @ts-nocheck
if (!customElements.get('parts-slider-loader')) {
  customElements.define(
    'parts-slider-loader',
    class PartsSliderLoader extends HTMLElement {
      /** @type {AbortController | null} */
      #abortController = null;
      #currentSku = '';
      /** @type {any} */
      #swiperInstance = null;
      /** @type {EventListener | null} */
      #variantHandler = null;
      /** @type {EventListener | null} */
      #cartHandler = null;

      connectedCallback() {
        this.#currentSku = this.dataset.sku || '';
        this.#fetchParts(this.#currentSku);

        /** @type {EventListener} */
        const handler = (/** @type {any} */ e) => {
          if (e.detail?.data?.newProduct) return;
          const sku = e.detail?.resource?.sku;
          if (!sku || sku === this.#currentSku) return;
          this.#currentSku = sku;
          this.#fetchParts(sku);
        };
        this.#variantHandler = handler;

        (/** @type {any} */ (document)).addEventListener('variant:update', handler);

        /** @type {EventListener} */
        const cartHandler = () => this.#syncCartQuantities();
        this.#cartHandler = cartHandler;
        (/** @type {any} */ (document)).addEventListener('cart:update', cartHandler);
      }

      disconnectedCallback() {
        this.#abortController?.abort();
        if (this.#variantHandler) (/** @type {any} */ (document)).removeEventListener('variant:update', this.#variantHandler);
        if (this.#cartHandler) (/** @type {any} */ (document)).removeEventListener('cart:update', this.#cartHandler);
        this.#swiperInstance?.destroy(true, true);
      }

      async #fetchParts(sku) {
        this.#abortController?.abort();
        this.#abortController = new AbortController();

        const handle = this.dataset.collectionHandle;
        const tag = (sku || '').trim().toLowerCase();
        // Without a tag the URL degenerates to the bare collection, which renders every
        // product in it — the same wrong-parts symptom as an unknown tag.
        if (!handle || !tag) { this.#renderEmpty(); return; }

        try {
          // Use Shopify's tag URL for exact server-side filtering — returns all products
          // with this exact tag, unlike ?q= which does partial match and limits results.
          // When the tag is absent from the collection Shopify 301s to the untagged
          // collection URL; collection.parts-data detects that and returns [].
          const url = `/collections/${handle}/${encodeURIComponent(tag)}?view=parts-data`;
          const res = await fetch(url, { signal: this.#abortController.signal });
          const parts = JSON.parse((await res.text()).trim());
          Array.isArray(parts) && parts.length > 0 ? this.#renderParts(parts, tag) : this.#renderEmpty();
        } catch (/** @type {any} */ err) {
          if (err.name !== 'AbortError') this.#renderEmpty();
        }
      }

      #renderParts(parts, tag) {
        this.#swiperInstance?.destroy(true, true);
        this.#swiperInstance = null;

        const sliderId = this.dataset.sliderId;
        const handle = this.dataset.collectionHandle;
        // Same normalised tag the fetch used, so "View all" can only ever point at a tag
        // that just returned results — never at the unfiltered category.
        const viewAllUrl = `/collections/${handle}/${encodeURIComponent(tag)}?page=1`;
        const displayParts = parts.slice(0, 3);

        this.innerHTML = `
          <div class="parts-slider-wrapper" style="position: relative;">
            <div class="parts-slider__navigation">
              <button
                class="slideshow-control slideshow-control--previous slideshow-control--shape-circle slideshow-control--style-arrow button button-unstyled button-unstyled--transparent flip-x"
                aria-label="Previous"
                disabled
                style="opacity: 0; pointer-events: none;"
              >
                <span class="svg-wrapper icon-arrow">
                  <svg width="19" height="16" viewBox="0 0 19 16" fill="none" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true">
                    <path d="M1 8H18M18 8L11 1M18 8L11 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
              </button>
              <button
                class="slideshow-control slideshow-control--next slideshow-control--shape-circle slideshow-control--style-arrow button button-unstyled"
                aria-label="Next"
                style="opacity: ${displayParts.length > 1 ? '1' : '0'}; pointer-events: ${displayParts.length > 1 ? 'auto' : 'none'};"
                ${displayParts.length <= 1 ? 'disabled' : ''}
              >
                <span class="svg-wrapper icon-arrow">
                  <svg width="19" height="16" viewBox="0 0 19 16" fill="none" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true">
                    <path d="M1 8H18M18 8L11 1M18 8L11 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
              </button>
            </div>
            <div class="swiper parts-slider__swiper" id="${sliderId}">
              <div class="swiper-wrapper">
                ${displayParts.map((p) => this.#cardHtml(p)).join('')}
              </div>
            </div>
          </div>
          <a href="${viewAllUrl}" class="parts-slider__view-all-button">View all parts for this machine</a>
        `;

        this.classList.remove('parts-slider-container--empty');
        this.dataset.partsEmpty = 'false';
        this.#updateAccordion(true);
        this.#initSwiper(sliderId, displayParts.length);
      }

      #renderEmpty() {
        this.#swiperInstance?.destroy(true, true);
        this.#swiperInstance = null;
        this.innerHTML = '<div class="parts-slider__empty-state" aria-hidden="true">No parts for this variant</div>';
        this.classList.add('parts-slider-container--empty');
        this.dataset.partsEmpty = 'true';
        this.#updateAccordion(false);
      }

      #cardHtml(part) {
        // Read SVG icons from the <template> element written by Liquid
        const templateId = this.dataset.iconsTemplateId || '';
        const templateEl = /** @type {HTMLTemplateElement | null} */ (document.getElementById(templateId));
        const cartIcon = templateEl?.content.querySelector('.parts-cart-icon')?.innerHTML || '';
        const checkmarkIcon = templateEl?.content.querySelector('.parts-checkmark-icon')?.innerHTML || '';
        const atcAnimation = this.dataset.addToCartAnimation || '';
        const formId = `Parts-ProductForm-${part.id}`;
        const qtyMax = part.qty_max || null;
        const atMaxQty = qtyMax !== null && qtyMax <= 1;

        const imageHtml = part.image
          ? `<div class="parts-product-card__image">
               <a href="${part.url}"><img src="${part.image}" alt="${this.#esc(part.image_alt)}" loading="lazy"></a>
             </div>`
          : '';

        const discountHtml = part.discount_formatted
          ? `<div class="parts-product-card__compare-price-container">
               <span class="parts-product-card__discount-badge">-${part.discount_formatted}</span>
               <s class="parts-product-card__compare-price">${part.compare_at_price_formatted}</s>
             </div>`
          : '';

        return `
          <div class="swiper-slide parts-slider__slide" style="margin-right: 15px;" data-space-between="15">
            <div class="parts-product-card">
              ${imageHtml}
              <div class="parts-product-card__content">
                <div class="parts-product-card__header">
                  <div class="parts-product-card__title">
                    <a href="${part.url}">${this.#esc(part.title)}</a>
                  </div>
                </div>
                <div class="parts-product-card__price-row">
                  <span class="parts-product-card__price">${part.price_formatted}</span>
                  ${discountHtml}
                </div>
              </div>
              <div class="parts-product-card__add">
                <span class="buy-buttons-block parts-product-card__buy-buttons">
                  <product-form-component
                    data-section-id="parts-slider"
                    data-product-id="${part.id}"
                    data-product-url="${this.#esc(part.url)}"
                    on:submit="/handleSubmit"
                    data-quantity-default="1"
                  >
                    <div class="visually-hidden" aria-live="assertive" role="status" aria-atomic="true" ref="liveRegion"></div>
                    <form action="/cart/add" method="post" data-type="add-to-cart-form" id="${formId}">
                      <input type="hidden" name="id" ref="variantId" value="${part.variant_id}">
                      <div class="product-form-buttons spacing-style">
                        <quantity-selector-component class="quantity-selector has-quantity-1${atMaxQty ? ' has-max-quantity' : ''}" ref="quantitySelectors[]" data-variant-id="${part.variant_id}">
                          <button type="button" class="button quantity-minus button-unstyled" on:click="/decreaseQuantity" ref="quantityButtons[]" disabled>
                            <span class="visually-hidden">Decrease</span>
                            <span class="svg-wrapper icon-plus">−</span>
                          </button>
                          <input type="number" name="quantity" value="1" min="1" ${qtyMax ? `max="${qtyMax}"` : ''} on:blur="/setQuantity" on:focus="/selectInputValue" ref="quantityInput" aria-label="Quantity">
                          <button type="button" class="button quantity-plus button-unstyled" on:click="/increaseQuantity" ref="quantityButtons[]" ${atMaxQty ? 'disabled' : ''}>
                            <span class="visually-hidden">Increase</span>
                            <span class="svg-wrapper icon-plus">+</span>
                          </button>
                        </quantity-selector-component>
                        <span style="--add-to-cart-font-case: ;">
                          <add-to-cart-component ref="addToCartButtonContainer" data-product-variant-media="${part.thumbnail || ''}" data-add-to-cart-animation="${atcAnimation}">
                            <button id="${formId}-submit" type="submit" name="add" ref="addToCartButton" on:click="/handleClick" class="button add-to-cart-button">
                              <span class="add-to-cart-text">
                                <span class="svg-wrapper add-to-cart-icon">${cartIcon}</span>
                                <span class="add-to-cart-text__content">Add</span>
                              </span>
                              <span aria-hidden="true" class="add-to-cart-text--added">
                                <span class="svg-wrapper add-to-cart-icon--added">${checkmarkIcon}</span>
                                <span>ADDED TO CART</span>
                              </span>
                            </button>
                          </add-to-cart-component>
                        </span>
                      </div>
                    </form>
                  </product-form-component>
                </span>
              </div>
            </div>
          </div>`;
      }

      async #syncCartQuantities() {
        try {
          const res = await fetch('/cart.js');
          const cart = await res.json();
          const items = cart.items || [];

          this.querySelectorAll('quantity-selector-component[data-variant-id]').forEach((qs) => {
            const variantId = Number(qs.dataset.variantId);
            const cartItem = items.find((item) => item.variant_id === variantId);
            const cartQty = cartItem?.quantity ?? 0;
            const displayQty = cartQty > 0 ? cartQty : 1;

            const input = /** @type {HTMLInputElement | null} */ (qs.querySelector('input[name="quantity"]'));
            const minusBtn = /** @type {HTMLButtonElement | null} */ (qs.querySelector('.quantity-minus'));
            const plusBtn = /** @type {HTMLButtonElement | null} */ (qs.querySelector('.quantity-plus'));
            const qtyMax = input?.getAttribute('max') ? parseInt(/** @type {string} */ (input.getAttribute('max'))) : null;

            if (input) input.value = String(displayQty);

            qs.classList.toggle('has-quantity-1', displayQty <= 1);
            qs.classList.toggle('has-max-quantity', qtyMax !== null && displayQty >= qtyMax);

            if (minusBtn) minusBtn.disabled = displayQty <= 1;
            if (plusBtn) plusBtn.disabled = qtyMax !== null && displayQty >= qtyMax;
          });
        } catch {
          // silent fail — cart sync is best-effort
        }
      }

      #updateAccordion(show) {
        const accordion = this.closest('accordion-custom');
        if (!accordion) return;
        accordion.classList.toggle('parts-accordion--hidden', !show);
        accordion.classList.toggle('parts-accordion--visible', show);
      }

      #initSwiper(sliderId, slideCount) {
        if (typeof Swiper === 'undefined') {
          setTimeout(() => this.#initSwiper(sliderId, slideCount), 100);
          return;
        }

        const el = this.querySelector(`#${sliderId}`);
        if (!el) return;

        this.#swiperInstance = new Swiper(`#${sliderId}`, {
          slidesPerView: 'auto',
          spaceBetween: 15,
          loop: false,
          speed: 400,
          breakpoints: { 0: { spaceBetween: 10 }, 750: { spaceBetween: 15 } },
        });

        const prev = /** @type {HTMLButtonElement | null} */ (this.querySelector('.slideshow-control--previous'));
        const next = /** @type {HTMLButtonElement | null} */ (this.querySelector('.slideshow-control--next'));

        if (prev && next) {
          prev.addEventListener('click', () => this.#swiperInstance?.slidePrev());
          next.addEventListener('click', () => this.#swiperInstance?.slideNext());
          const update = () => this.#updateNavBtns(prev, next);
          this.#swiperInstance.on('slideChange', update);
          this.#swiperInstance.on('reachEnd', update);
          this.#swiperInstance.on('reachBeginning', update);
          update();
        }
      }

      #updateNavBtns(prev, next) {
        const s = this.#swiperInstance;
        if (!s) return;
        prev.disabled = s.isBeginning;
        prev.style.opacity = s.isBeginning ? '0' : '1';
        prev.style.pointerEvents = s.isBeginning ? 'none' : 'auto';
        next.disabled = s.isEnd;
        next.style.opacity = s.isEnd ? '0' : '1';
        next.style.pointerEvents = s.isEnd ? 'none' : 'auto';
      }

      #esc(str) {
        return String(str ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
      }
    }
  );
}
